import { useCallback, useEffect } from 'react';
import { useZipCodeStore } from '@common/hooks';
import {
  FieldValues,
  UseFormSetValue,
  UseFormWatch,
  Path,
  PathValue,
  UseFormGetValues,
} from 'react-hook-form';

interface RHFFieldNames<T> {
  zipCode: Path<T>;
  city: Path<T>;
  county: Path<T>;
  stateId: Path<T>;
}

const getNestedValue = (obj: any, path: string): string | number => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};
/**
 * This hook updates city, county and state based on a valid zipcode.
 * IMPORTANT: It is CRUCIAL that ZipCodeStoreContextWrapper.set(new ZipCodeStore()); is set in the parent component.
 * @param watch
 * @param setValue
 * @param fieldNames
 * @param shouldDirty
 * @param shouldValidate
 */
export const useZipCodeChanged = <T extends FieldValues>(
  watch: UseFormWatch<T>,
  setValue: UseFormSetValue<T>,
  getValues: UseFormGetValues<T>,
  fieldNames: RHFFieldNames<T>,
  shouldDirty = true,
  shouldValidate = false,
  updateStoreField?: (path: string, value: PathValue<T, Path<T>>) => void | undefined,
) => {
  const zipCodeStore = useZipCodeStore();
  useEffect(() => {
    // Call onZipCodeChanged when the zip code changes.
    const subscription = watch((value, { name, type }) => {
      const zipCodeField = getNestedValue(value, fieldNames.zipCode);
      if (
        name === fieldNames.zipCode &&
        type === 'change' &&
        !_.isNil(zipCodeField)
      )
        zipCodeStore.fetchZipCode(zipCodeField.toString() ?? '')
          .then(updateFields);
    });
    return () => subscription.unsubscribe();
  }, [watch, zipCodeStore, fieldNames]);


  const updateFields = useCallback(() => {
    const fetchedData = zipCodeStore.zipCode;

    if (!fetchedData) {
      return;
    }

    const options = { shouldDirty, shouldValidate };

    checkAndSetValue('city', fetchedData.city as PathValue<T, Path<T>>, options);
    checkAndSetValue('county', fetchedData.county as PathValue<T, Path<T>>, options);
    checkAndSetValue('stateId', fetchedData.stateId as PathValue<T, Path<T>>, options);

  }, [
    zipCodeStore.zipCode,
    getValues,
    setValue,
    fieldNames,
    shouldDirty,
    shouldValidate,
  ]);

  const checkAndSetValue = useCallback(
    (
      fieldNameKey: keyof RHFFieldNames<T>,
      fetchedValue: PathValue<T, Path<T>>,
      options: {
        shouldDirty: boolean;
        shouldValidate: boolean;
      },
    ) => {
      const fieldName = fieldNames[fieldNameKey] as Path<T>;
      const currentValue = getValues(fieldName);

      if (currentValue !== fetchedValue) {
        setValue(fieldName, fetchedValue, options);

        if (updateStoreField) {
          updateStoreField(fieldName, fetchedValue);
        }
      }
    },
    [getValues, setValue, fieldNames, updateStoreField],
  );
};
